import { useRef, useState, type FormEvent } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  parseDatum,
  alsEingabetext,
  formatDatum,
  type UnscharfesDatum,
} from "@stammbaum/shared";
import { api, bildUrl, dateiUrl, istPdf } from "../api";
import { DatumFeld } from "../komponenten/DatumFeld";
import { PersonSucher } from "../komponenten/PersonSucher";
import { Markdown } from "../komponenten/Markdown";
import { useTitel } from "../titel";
import { familieText, ereignisText } from "../komponenten/bezeichnungen";
import { OrtFeld } from "../komponenten/OrtFeld";

/** „8,4 MB", „612 kB". Eine Nachkommastelle genuegt, um zu entscheiden. */
function bytesKurz(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(".", ",")} MB`;
  if (bytes >= 1000) return `${Math.round(bytes / 1000)} kB`;
  return `${bytes} B`;
}

interface Ausschnitt {
  x: number;
  y: number;
  breite: number;
  hoehe: number;
}

/**
 * Bildansicht mit Markierungen.
 *
 * Ein Rechteck über ein Gesicht ziehen, Person zuordnen — fertig. Der
 * Ausschnitt wird relativ zur Bildgröße gespeichert (0–1), nicht in Pixeln.
 * Deshalb stimmt er in jeder abgeleiteten Größe, und aus einem Gruppenfoto
 * werden die Portraits aller Abgebildeten, ohne das Bild zu vervielfältigen.
 */
export default function FotoSeite() {
  const { id } = useParams();
  const medienId = Number(id);
  const client = useQueryClient();
  const navigate = useNavigate();
  const bildRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [zuordnen, setZuordnen] = useState(false);

  /**
   * Markiermodus - wegen der Finger.
   *
   * Am Touchgeraet ist die Ein-Finger-Bewegung schon vergeben: sie
   * scrollt. Ein Kasten, der sie fuer sich beansprucht (`touch-action:
   * none`), laesst sich nicht mehr wegscrollen - und ein bildschirmbreites
   * Foto ist am Handy die halbe Seite. Also ein Schalter: aus wird
   * gescrollt, an wird gezeichnet.
   *
   * Mit Maus und Stift bleibt es beim direkten Ziehen, mit oder ohne
   * Schalter. Die brauchen keine Gestensperre.
   */
  const [markieren, setMarkieren] = useState(false);
  const [zieht, setZieht] = useState<{ vonX: number; vonY: number } | null>(null);
  const [entwurf, setEntwurf] = useState<Ausschnitt | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  /** Offene Auswahl: Familie oder Ereignis zuordnen. */
  const [zielWahl, setZielWahl] = useState<null | "familie" | "ereignis">(null);

  const daten = useQuery({ queryKey: ["medium", medienId], queryFn: () => api.medium(medienId) });

  /*
   * Vor allen fruehen Returns. Der Titel nimmt, was da ist: den
   * vergebenen Titel, sonst den Dateinamen - genau wie die Ueberschrift
   * der Seite.
   */
  useTitel(
    daten.data
      ? `${daten.data.medium.titel || daten.data.medium.dateiname} (${medienId})`
      : `Bild ${medienId}`,
  );

  // Erst laden, wenn jemand zuordnen will - vorher braucht es die Liste nicht.
  const ziele = useQuery({
    queryKey: ["medienZiele", medienId],
    queryFn: () => api.medienZiele(medienId),
    enabled: zielWahl !== null,
  });

  function neuLaden() {
    client.invalidateQueries({ queryKey: ["medium", medienId] });
    client.invalidateQueries({ queryKey: ["medienZiele", medienId] });
    client.invalidateQueries({ queryKey: ["person"] });
    client.invalidateQueries({ queryKey: ["personen"] });
    client.invalidateQueries({ queryKey: ["baum"] });
    // Der Aufnahmeort kann beim Speichern einen neuen Ort angelegt haben -
    // dann gehört er auch in die Vorschlagslisten der anderen Masken.
    client.invalidateQueries({ queryKey: ["orte"] });
  }

  /**
   * Mausposition in Anteile der Bildbreite/-höhe umrechnen.
   *
   * Gemessen wird am Bild selbst, nicht am umgebenden Kasten. Die Zahlen
   * landen so in der Datenbank - ist der Bezug falsch, sind die Markierungen
   * dauerhaft falsch gespeichert und kein späteres Zurechtrücken hilft.
   */
  function relativ(e: { clientX: number; clientY: number }) {
    const k = (imgRef.current ?? bildRef.current)!.getBoundingClientRect();
    if (!k.width || !k.height) return { x: 0, y: 0 };
    return {
      x: Math.min(Math.max((e.clientX - k.left) / k.width, 0), 1),
      y: Math.min(Math.max((e.clientY - k.top) / k.height, 0), 1),
    };
  }

  async function tagSpeichern(person: { id: number; vorname: string; famname: string } | null) {
    if (!entwurf) return;
    setFehler(null);
    try {
      await api.tagAnlegen(medienId, { ...entwurf, personId: person?.id ?? null });
      setEntwurf(null);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Speichern fehlgeschlagen.");
    }
  }

  async function tagLoeschen(tagId: number) {
    if (!confirm("Markierung entfernen?")) return;
    try {
      await api.tagLoeschen(medienId, tagId);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Löschen fehlgeschlagen.");
    }
  }

  async function alsPortrait(personId: number, tagId: number | null) {
    try {
      await api.portraitSetzen(personId, medienId, tagId);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Konnte Portrait nicht setzen.");
    }
  }

  async function zuordnungLoesen(personId: number, name: string) {
    if (
      !confirm(
        `Dieses Bild gehört nicht zu ${name}?\n\n` +
          "Die Zuordnung wird gelöst, ebenso Markierungen dieser Person auf " +
          "dem Bild und ein Portrait, das darauf zeigt.\n" +
          "Das Bild selbst bleibt erhalten.",
      )
    )
      return;
    setFehler(null);
    try {
      await api.zuordnungLoesen(medienId, "person", personId);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Lösen fehlgeschlagen.");
    }
  }

  async function personZuordnen(person: { id: number; vorname: string; famname: string }) {
    setZuordnen(false);
    setFehler(null);
    try {
      await api.medienVerknuepfen(medienId, "person", person.id);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Zuordnen fehlgeschlagen.");
    }
  }

  /** Bild an eine Familie oder ein Ereignis hängen. */
  async function zielZuordnen(typ: "familie" | "ereignis", zielId: number) {
    setZielWahl(null);
    setFehler(null);
    try {
      await api.medienVerknuepfen(medienId, typ, zielId);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Zuordnen fehlgeschlagen.");
    }
  }

  async function zielLoesen(typ: "familie" | "ereignis", zielId: number, text: string) {
    if (!confirm(`Zuordnung zu „${text}" lösen?\n\nDas Bild selbst bleibt erhalten.`)) return;
    setFehler(null);
    try {
      await api.zuordnungLoesen(medienId, typ, zielId);
      neuLaden();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Lösen fehlgeschlagen.");
    }
  }

  async function bildLoeschen() {
    if (
      !confirm(
        "Dieses Bild ganz entfernen?\n\n" +
          "Alle Markierungen und Zuordnungen verschwinden mit. Die " +
          "Originaldatei bleibt auf der Platte und kann erneut hochgeladen " +
          "werden.",
      )
    )
      return;
    try {
      await api.medienLoeschen(medienId);
      neuLaden();
      navigate(-1);
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Löschen fehlgeschlagen.");
    }
  }

  if (daten.error) return <p className="text-red-700">{(daten.error as Error).message}</p>;
  if (!daten.data) return <p className="text-black/40">lädt …</p>;

  const { medium, tags, portraits, zugeordnetePersonen, zugeordneteFamilien, zugeordneteEreignisse } =
    daten.data;
  // Eine Urkunde ist kein Gruppenfoto: nichts zu markieren, kein Portrait.
  const pdf = istPdf(medium.mimetyp);
  const seiten = (medium.exif as { seiten?: number } | null)?.seiten ?? null;

  const istPortraitVon = (tagId: number | null) =>
    portraits.filter((p) => (p.tagId ?? null) === tagId).map((p) => p.personId);

  const prozent = (w: number) => `${w * 100}%`;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {medium.titel || medium.dateiname}
          </h1>
          <p className="text-sm text-black/50 dark:text-white/50">
            {aufnahmeDatum(medium) && (
              <>
                Aufgenommen {formatDatum(aufnahmeDatum(medium)!)}
                {medium.ortName && ` in ${medium.ortName}`}.{" "}
              </>
            )}
            {pdf
              ? "Urkunde. Die Vorschau zeigt die erste Seite — das vollständige Dokument über „Original öffnen“."
              : "Ein Rechteck über ein Gesicht ziehen, dann die Person auswählen."}
            {pdf && seiten ? ` ${seiten} Seiten.` : ""}
          </p>
        </div>
        {/*
          Beides zeigt auf dieselbe Route: die unveraenderte Datei aus dem
          Archiv. Vorher stand hier bei Bildern die 1200er Ansicht - ein
          webp mit Qualitaet 82, also zweimal verlustbehaftet gegenueber dem
          Scan. Fuer ein Familienfoto reicht das; um die Handschrift auf der
          Rueckseite zu lesen, nicht.

          Die Groesse steht dabei, denn das Original kann zweistellige
          Megabyte haben und jemand schaut ueber Mobilfunk.
        */}
        <div className="flex shrink-0 flex-col items-end gap-1 text-sm">
          <a
            href={dateiUrl(medium.id)}
            target="_blank"
            rel="noreferrer"
            className="text-black/60 underline dark:text-white/60"
          >
            {pdf ? "Original öffnen (PDF)" : "Original ansehen"}
            {medium.bytes ? (
              <span className="ml-1 text-xs text-black/40 dark:text-white/40">
                {!pdf && medium.breite ? `${medium.breite} × ${medium.hoehe}, ` : ""}
                {bytesKurz(medium.bytes)}
              </span>
            ) : null}
          </a>
          <a
            href={dateiUrl(medium.id, true)}
            className="text-black/60 underline dark:text-white/60"
          >
            herunterladen
          </a>
          {!pdf && (
            <button
              onClick={() => {
                setMarkieren(!markieren);
                setZieht(null);
              }}
              aria-pressed={markieren}
              className={
                "mt-1 rounded-lg border px-2 py-1 text-xs " +
                (markieren
                  ? "border-amber-500 bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
                  : "border-black/15 text-black/60 hover:border-black/40 dark:border-white/15 dark:text-white/60")
              }
            >
              {markieren ? "Markieren beenden" : "Markieren"}
            </button>
          )}
        </div>
      </div>

      {markieren && (
        <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          Ein Rechteck über ein Gesicht ziehen — am Handy mit dem Finger. Solange
          „Markieren" an ist, scrollt das Bild nicht mit.
        </p>
      )}

      {fehler && (
        <p role="alert" className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </p>
      )}

      {/*
        items-start ist hier kein Schönheitsfehler, sondern Pflicht: ohne das
        streckt die Flexbox die Bildspalte auf die Höhe der Seitenspalte. Die
        Markierungen sitzen in Prozent dieses Kastens - ein gestreckter Kasten
        schiebt sie nach unten, und schlimmer: neu gezogene Rechtecke werden
        gegen den gestreckten Kasten gerechnet und damit falsch gespeichert.
      */}
      <div className="flex flex-wrap items-start gap-6">
        <div className="max-w-3xl flex-1">
        <div
          ref={bildRef}
          className="relative select-none"
          style={{
            cursor: pdf ? "default" : entwurf ? "default" : "crosshair",
            /**
             * NUR im Markiermodus die Gesten an uns ziehen. Ohne
             * `touch-action: none` schickt der Browser nach dem ersten
             * Millimeter Fingerbewegung ein `pointercancel` und scrollt
             * stattdessen - `pointermove` kommt nie an, und am Handy liess
             * sich keine Markierung ziehen. Dauerhaft gesetzt waere es
             * aber der umgekehrte Fehler: dann scrollt die Seite nicht
             * mehr, solange der Finger auf dem Foto liegt.
             */
            touchAction: markieren && !pdf ? "none" : undefined,
          }}
          onPointerDown={(e) => {
            if (pdf) return; // an einer Urkunde gibt es nichts zu markieren
            if (entwurf) return; // erst den offenen Entwurf abschließen
            // Der Finger zeichnet nur im Markiermodus, Maus und Stift immer.
            if (e.pointerType === "touch" && !markieren) return;
            // `currentTarget` und nicht `target`: die Erfassung gehoert an
            // den Kasten mit den Handlern, nicht an das Bild darin.
            e.currentTarget.setPointerCapture?.(e.pointerId);
            const p = relativ(e);
            setZieht({ vonX: p.x, vonY: p.y });
          }}
          onPointerMove={(e) => {
            if (!zieht) return;
            const p = relativ(e);
            setEntwurf({
              x: Math.min(zieht.vonX, p.x),
              y: Math.min(zieht.vonY, p.y),
              breite: Math.abs(p.x - zieht.vonX),
              hoehe: Math.abs(p.y - zieht.vonY),
            });
          }}
          onPointerUp={() => {
            setZieht(null);
            // Winzige Rechtecke sind Fehlklicks, keine Markierungen.
            if (entwurf && (entwurf.breite < 0.02 || entwurf.hoehe < 0.02)) setEntwurf(null);
          }}
          /**
           * Bricht das System die Geste ab - ein zweiter Finger, ein Anruf,
           * eine Zurueck-Wischbewegung am Rand -, bliebe `zieht` sonst
           * stehen und die naechste Bewegung ueber dem Bild zoge weiter an
           * einem Rechteck, das man gar nicht mehr angefasst hat.
           */
          onPointerCancel={() => {
            setZieht(null);
            setEntwurf(null);
          }}
        >
          <img
            ref={imgRef}
            src={bildUrl(medium.id, 1200)}
            alt={medium.dateiname}
            draggable={false}
            className="block w-full rounded-lg"
          />

          {tags.map(({ tag, person }) => (
            <div
              key={tag.id}
              className="group absolute rounded border-2 border-amber-400/90"
              style={{
                left: prozent(tag.x),
                top: prozent(tag.y),
                width: prozent(tag.breite),
                height: prozent(tag.hoehe),
              }}
            >
              <span
                className="absolute left-0 top-full mt-0.5 whitespace-nowrap rounded bg-amber-400/95
                           px-1.5 py-0.5 text-xs text-black"
              >
                {person ? `${person.vorname} ${person.famname}` : (tag.beschriftung ?? "wer?")}
              </span>
            </div>
          ))}

          {entwurf && (
            <div
              className="absolute rounded border-2 border-dashed border-sky-500 bg-sky-500/10"
              style={{
                left: prozent(entwurf.x),
                top: prozent(entwurf.y),
                width: prozent(entwurf.breite),
                height: prozent(entwurf.hoehe),
              }}
            />
          )}

          {entwurf && !zieht && (
            <div
              className="absolute z-10"
              style={{ left: prozent(entwurf.x), top: `calc(${prozent(entwurf.y + entwurf.hoehe)} + 6px)` }}
            >
              <PersonSucher onWahl={tagSpeichern} onAbbrechen={() => setEntwurf(null)} />
              <button
                onClick={() => tagSpeichern(null)}
                className="mt-1 w-72 rounded-lg border border-black/15 bg-white px-2 py-1 text-xs
                           hover:border-black/40 dark:border-white/15 dark:bg-neutral-800"
              >
                ohne Zuordnung speichern
              </button>
            </div>
          )}
        </div>

          {/*
            Die Beschreibung wurde bisher gespeichert, aber nirgends
            gezeigt - wer eine Abschrift hineinschrieb, schrieb ins Leere.
            Sie steht hier, direkt unter dem Scan, damit man beim Lesen
            vergleichen kann. Dass derselbe Text darunter nochmals im
            Eingabefeld steht, ist der Preis dafuer, dass die Maske auf
            dieser Seite immer offen ist.
          */}
          {medium.beschreibung?.trim() && (
            <section className="mt-5">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-black/50">
                {pdf ? "Transkription" : "Beschreibung"}
              </h2>
              <Markdown text={medium.beschreibung} klasse="space-y-2 text-sm leading-relaxed" />
            </section>
          )}

          {/*
            `key`: ohne das bleibt BildAngaben beim Wechsel von Bild A zu
            Bild B montiert und behaelt seinen Zustand. Liegt das Ziel im
            Zwischenspeicher (staleTime 30 s), gibt es nicht einmal einen
            Ladezustand dazwischen - im Textfeld stuende Bs Beschreibung
            ueber As Scan, und „Angaben speichern" schriebe sie auf A.
            Dieselbe Vorkehrung wie bei <Verwandtschaft> auf der
            Personenseite.
          */}
          <BildAngaben key={medium.id} medium={medium} onFertig={neuLaden} />
        </div>

        <div className="w-72">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-black/50">
            {pdf ? "Dokument" : `Markierungen (${tags.length})`}
          </h2>

          {pdf && (
            <p className="mb-4 text-sm text-black/50">
              Eine Urkunde wird nicht markiert und gibt kein Portrait ab. Sie
              gehört aber genauso zu Personen, Familien und Ereignissen — siehe
              unten.{" "}
              <a
                href={dateiUrl(medium.id)}
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-black dark:hover:text-white"
              >
                Original öffnen
              </a>
              .
            </p>
          )}

          {!pdf && tags.length === 0 && (
            <p className="text-sm text-black/50">
              Noch keine. Zieh ein Rechteck über ein Gesicht.
            </p>
          )}

          <ul className="space-y-2">
            {tags.map(({ tag, person }) => {
              const portraitVon = istPortraitVon(tag.id);
              return (
                <li
                  key={tag.id}
                  className="flex items-center gap-2 rounded-lg border border-black/10 p-2 dark:border-white/10"
                >
                  <img
                    src={bildUrl(medium.id, 64, tag.id)}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-full object-cover"
                  />
                  <div className="min-w-0 flex-1 text-sm">
                    {person ? (
                      <Link to={`/person/${person.id}`} className="hover:underline">
                        {person.vorname} {person.famname}
                      </Link>
                    ) : (
                      <span className="text-black/50">ohne Zuordnung</span>
                    )}
                    {person && portraitVon.includes(person.id) && (
                      <div className="text-xs text-amber-700">ist das Portrait</div>
                    )}
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    {person && !portraitVon.includes(person.id) && (
                      <button
                        onClick={() => alsPortrait(person.id, tag.id)}
                        title="als Portrait dieser Person verwenden"
                        className="rounded border border-black/15 px-1.5 py-0.5 text-xs
                                   hover:border-black/40 dark:border-white/15"
                      >
                        Portrait
                      </button>
                    )}
                    <button
                      onClick={() => tagLoeschen(tag.id)}
                      title="Markierung entfernen"
                      className="rounded border border-black/15 px-1.5 py-0.5 text-xs text-red-700
                                 hover:border-red-400 dark:border-white/15"
                    >
                      löschen
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {!pdf && (
            <p className="mt-4 text-xs text-black/40 dark:text-white/40">
              Aus jeder Markierung entsteht das Portrait der zugeordneten Person.
              Das Original bleibt unverändert — die Ausschnitte werden bei Bedarf
              berechnet.
            </p>
          )}

          <h2 className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wide text-black/50">
            Gehört zu
          </h2>
          <p className="mb-3 text-xs text-black/40 dark:text-white/40">
            Wo das Bild auftaucht. Ein Hochzeitsfoto gehört der Familie, ein
            Begräbnisfoto dem Ereignis — dann muss es nicht bei jedem einzeln
            hängen.
          </p>

          <h3 className="mb-1 text-xs font-medium text-black/60 dark:text-white/60">
            Personen ({zugeordnetePersonen.length})
          </h3>

          <ul className="space-y-1 text-sm">
            {zugeordnetePersonen.map((person) => (
              <li key={person.id} className="flex items-center gap-2">
                <Link to={`/person/${person.id}`} className="flex-1 hover:underline">
                  {person.famname} {person.vorname}
                  <span className="ml-1 text-xs text-black/40 dark:text-white/40">
                    {person.geburtVon?.slice(0, 4) ?? ""}
                  </span>
                </Link>
                <button
                  onClick={() => zuordnungLoesen(person.id, `${person.vorname} ${person.famname}`)}
                  title="gehört nicht zu dieser Person"
                  className="rounded border border-black/15 px-1.5 py-0.5 text-xs text-red-700
                             hover:border-red-400 dark:border-white/15"
                >
                  lösen
                </button>
              </li>
            ))}
            {zugeordnetePersonen.length === 0 && (
              <li className="text-black/50">Niemandem zugeordnet.</li>
            )}
          </ul>

          {zuordnen ? (
            <div className="mt-2">
              <PersonSucher onWahl={personZuordnen} onAbbrechen={() => setZuordnen(false)} />
            </div>
          ) : (
            <button
              onClick={() => setZuordnen(true)}
              className="mt-2 rounded-lg border border-black/15 px-3 py-1 text-sm
                         hover:border-black/40 dark:border-white/15"
            >
              Person zuordnen
            </button>
          )}

          <h3 className="mb-1 mt-5 text-xs font-medium text-black/60 dark:text-white/60">
            Familien ({zugeordneteFamilien.length})
          </h3>
          <ul className="space-y-1 text-sm">
            {zugeordneteFamilien.map((f) => (
              <li key={f.id} className="flex items-start gap-2">
                <span className="flex-1">{familieText(f)}</span>
                <button
                  onClick={() => zielLoesen("familie", f.id, familieText(f))}
                  className="shrink-0 rounded border border-black/15 px-1.5 py-0.5 text-xs
                             text-red-700 hover:border-red-400 dark:border-white/15"
                >
                  lösen
                </button>
              </li>
            ))}
            {zugeordneteFamilien.length === 0 && (
              <li className="text-black/50">Keiner Familie zugeordnet.</li>
            )}
          </ul>

          {zielWahl === "familie" ? (
            <ZielListe
              frage="Zu welcher Familie gehört das Bild?"
              laedt={ziele.isFetching}
              eintraege={(ziele.data?.familien ?? [])
                .filter((f) => !zugeordneteFamilien.some((x) => x.id === f.id))
                .map((f) => ({ id: f.id, text: familieText(f) }))}
              onWahl={(zielId) => zielZuordnen("familie", zielId)}
              onAbbrechen={() => setZielWahl(null)}
            />
          ) : (
            <button
              onClick={() => setZielWahl("familie")}
              className="mt-2 rounded-lg border border-black/15 px-3 py-1 text-sm
                         hover:border-black/40 dark:border-white/15"
            >
              Familie zuordnen
            </button>
          )}

          <h3 className="mb-1 mt-5 text-xs font-medium text-black/60 dark:text-white/60">
            Ereignisse ({zugeordneteEreignisse.length})
          </h3>
          <ul className="space-y-1 text-sm">
            {zugeordneteEreignisse.map((e) => (
              <li key={e.id} className="flex items-start gap-2">
                <span className="flex-1">{ereignisText(e)}</span>
                <button
                  onClick={() => zielLoesen("ereignis", e.id, ereignisText(e))}
                  className="shrink-0 rounded border border-black/15 px-1.5 py-0.5 text-xs
                             text-red-700 hover:border-red-400 dark:border-white/15"
                >
                  lösen
                </button>
              </li>
            ))}
            {zugeordneteEreignisse.length === 0 && (
              <li className="text-black/50">Keinem Ereignis zugeordnet.</li>
            )}
          </ul>

          {zielWahl === "ereignis" ? (
            <ZielListe
              frage="Zu welchem Ereignis gehört das Bild?"
              laedt={ziele.isFetching}
              eintraege={(ziele.data?.ereignisse ?? [])
                .filter((e) => !zugeordneteEreignisse.some((x) => x.id === e.id))
                .map((e) => ({ id: e.id, text: ereignisText(e) }))}
              onWahl={(zielId) => zielZuordnen("ereignis", zielId)}
              onAbbrechen={() => setZielWahl(null)}
            />
          ) : (
            <button
              onClick={() => setZielWahl("ereignis")}
              className="mt-2 rounded-lg border border-black/15 px-3 py-1 text-sm
                         hover:border-black/40 dark:border-white/15"
            >
              Ereignis zuordnen
            </button>
          )}

          <button
            onClick={bildLoeschen}
            className="mt-8 block text-xs text-black/40 underline hover:text-red-700 dark:text-white/40"
          >
            Bild ganz entfernen
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Auswahl aus den in Frage kommenden Familien bzw. Ereignissen.
 *
 * Bewusst keine Suche über alle 500 Familien: angeboten wird nur, was zu den
 * Menschen auf diesem Bild gehört. Steht nichts da, fehlt die Markierung —
 * und genau das sagt der Hinweis dann auch.
 */
function ZielListe({
  frage,
  eintraege,
  laedt,
  onWahl,
  onAbbrechen,
}: {
  frage: string;
  eintraege: { id: number; text: string }[];
  laedt: boolean;
  onWahl: (id: number) => void;
  onAbbrechen: () => void;
}) {
  return (
    <div className="mt-2 rounded-lg border border-black/15 bg-white p-2 shadow-lg dark:border-white/15 dark:bg-neutral-800">
      <div className="flex items-baseline gap-2">
        <p className="flex-1 text-xs text-black/60 dark:text-white/60">{frage}</p>
        <button
          onClick={onAbbrechen}
          title="Abbrechen"
          className="px-1 text-black/40 hover:text-black dark:text-white/40"
        >
          ×
        </button>
      </div>

      {laedt && <p className="px-1 py-1 text-sm text-black/40">lädt …</p>}

      <ul className="mt-1 max-h-60 overflow-y-auto text-sm">
        {eintraege.map((e) => (
          <li key={e.id}>
            <button
              onClick={() => onWahl(e.id)}
              className="w-full rounded px-2 py-1 text-left hover:bg-black/5 dark:hover:bg-white/10"
            >
              {e.text}
            </button>
          </li>
        ))}
      </ul>

      {!laedt && eintraege.length === 0 && (
        <p className="px-1 py-1 text-xs text-black/50">
          Nichts zur Auswahl. Markiere zuerst jemanden auf dem Bild oder ordne
          es einer Person zu — dann stehen deren Familien und Ereignisse hier.
        </p>
      )}
    </div>
  );
}

/** Aus den Spalten des Bildes wieder ein unscharfes Datum machen. */
function aufnahmeDatum(m: {
  aufnahmeVon: string | null;
  aufnahmeBis: string | null;
  aufnahmeGenauigkeit: UnscharfesDatum["genauigkeit"] | null;
  aufnahmeText: string | null;
}): UnscharfesDatum | null {
  if (!m.aufnahmeVon && !m.aufnahmeText) return null;
  return {
    von: m.aufnahmeVon,
    bis: m.aufnahmeBis,
    genauigkeit: m.aufnahmeGenauigkeit ?? "jahr",
    text: m.aufnahmeText,
  };
}

/**
 * Angaben zum Bild: wann aufgenommen, wo, wie betitelt.
 *
 * Das Datum ist unscharf wie überall sonst — ein eingescanntes Foto ist
 * "um 1950". Steckt ein Zeitstempel in den Bilddaten, steht er als Vorschlag
 * darunter: die Kamera weiß, wann ausgelöst wurde, aber ein Scanner datiert
 * das Scannen. Deshalb wird er angeboten und nicht aufgezwungen.
 */
function BildAngaben({ medium, onFertig }: { medium: any; onFertig: () => void }) {
  const [titel, setTitel] = useState<string>(medium.titel ?? "");
  const [beschreibung, setBeschreibung] = useState<string>(medium.beschreibung ?? "");
  const [ortName, setOrtName] = useState<string>(medium.ortName ?? "");
  // Die Id gewinnt beim Speichern, solange niemand im Feld tippt: die Maske
  // zeigt nur die unterste Ebene, und derselbe Name kann in der Ortskette
  // mehrfach vorkommen.
  const [ortId, setOrtId] = useState<number | null>(medium.ortId ?? null);
  const [datumText, setDatumText] = useState<string>(alsEingabetext(aufnahmeDatum(medium)));
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [gespeichert, setGespeichert] = useState(false);


  // Zeitstempel aus den Bilddaten, falls vorhanden.
  const ausBilddaten = medium.aufgenommenAm ? new Date(medium.aufgenommenAm) : null;
  const ausBilddatenTag = ausBilddaten ? ausBilddaten.toISOString().slice(0, 10) : null;
  const kamera = [medium.exif?.hersteller, medium.exif?.modell].filter(Boolean).join(" ");

  /*
   * Steht im Feld schon genau dieser Tag? Der Vergleich muss ueber die
   * gedeutete Form laufen: im Feld steht "25.2.2026", in den Bilddaten
   * "2026-02-25". Als Zeichenketten sind die beiden nie gleich - der
   * Knopf "uebernehmen" stand deshalb auch noch da, wenn man ihn gerade
   * gedrueckt hatte.
   */
  const gedeutetesDatum = datumText.trim() ? parseDatum(datumText) : null;
  const schonUebernommen =
    ausBilddatenTag !== null &&
    gedeutetesDatum?.von === ausBilddatenTag &&
    gedeutetesDatum?.bis === ausBilddatenTag;

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    setLaeuft(true);
    try {
      await api.medienAendern(medium.id, {
        titel: titel.trim() || null,
        beschreibung: beschreibung.trim() || null,
        aufnahme: datumText.trim() ? parseDatum(datumText) : null,
        ortName: ortName.trim() || null,
        ortId,
      });
      setGespeichert(true);
      setTimeout(() => setGespeichert(false), 2000);
      onFertig();
    } catch (f) {
      setFehler(f instanceof Error ? f.message : "Speichern fehlgeschlagen.");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <form
      onSubmit={speichern}
      className="mt-4 rounded-lg border border-black/10 p-4 dark:border-white/10"
    >
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-black/50">
        Angaben zum Bild
      </h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <DatumFeld beschriftung="Aufgenommen" wert={datumText} onChange={setDatumText} />
          {/*
            Kein negativer Abstand mehr: `DatumFeld` haelt darunter eine
            Zeile fuer "gelesen als ..." frei, und sobald dort etwas stand,
            lagen die beiden Zeilen uebereinander.
          */}
          {ausBilddatenTag && (
            <p className="mt-1 text-xs text-black/45 dark:text-white/45">
              Aus den Bilddaten:{" "}
              <strong className="font-medium">
                {formatDatum({
                  von: ausBilddatenTag,
                  bis: ausBilddatenTag,
                  genauigkeit: "exakt",
                  text: null,
                })}
              </strong>
              {kamera && ` (${kamera})`}
              {!schonUebernommen && (
                <button
                  type="button"
                  onClick={() =>
                    setDatumText(
                      formatDatum({
                        von: ausBilddatenTag,
                        bis: ausBilddatenTag,
                        genauigkeit: "exakt",
                        text: null,
                      }),
                    )
                  }
                  className="ml-2 underline hover:text-black dark:hover:text-white"
                >
                  übernehmen
                </button>
              )}
            </p>
          )}
        </div>

        <OrtFeld
          beschriftung="Aufnahmeort"
          id="bild-orte-vorschlaege"
          wert={ortName}
          onChange={(t) => {
            setOrtName(t);
            setOrtId(null);
          }}
          platzhalter="wo wurde das aufgenommen?"
          klasseFeld="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 outline-none
                      focus:border-black/40 dark:border-white/15 dark:bg-neutral-800"
          klasseBeschriftung="text-sm text-black/60 dark:text-white/60"
        />

        <label className="block">
          <span className="text-sm text-black/60 dark:text-white/60">Titel</span>
          <input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            maxLength={200}
            placeholder="optional"
            className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 outline-none
                       focus:border-black/40 dark:border-white/15 dark:bg-neutral-800"
          />
        </label>

        {/*
          Ueber beide Spalten und hoch: hier steht bei einer Matrikenseite
          die Abschrift des Eintrags, nicht „Oma im Garten". Die Spalte ist
          `text()` und die API nimmt 5000 Zeichen - eingeengt hat das bis
          jetzt nur dieses Eingabefeld.
        */}
        <label className="block sm:col-span-2">
          <span className="text-sm text-black/60 dark:text-white/60">
            Beschreibung oder Transkription
          </span>
          <textarea
            value={beschreibung}
            onChange={(e) => setBeschreibung(e.target.value)}
            maxLength={5000}
            rows={10}
            placeholder={
              "optional — bei einer Matrikenseite die Abschrift des Eintrags.\n\n" +
              "**fett**, *kursiv*, `wörtlich`, > Zitat, - Aufzählung, [Text](https://…)\n" +
              "Tabelle: eine Zeile | Feld | Lesung | und darunter |---|---|"
            }
            className="mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 font-mono
                       text-sm outline-none focus:border-black/40 dark:border-white/15
                       dark:bg-neutral-800"
          />
          <span className="mt-1 block text-xs text-black/40 dark:text-white/40">
            {beschreibung.length} von 5000 Zeichen
          </span>
        </label>
      </div>

      {fehler && (
        <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
          {fehler}
        </p>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button
          type="submit"
          disabled={laeuft}
          className="rounded-lg bg-tinte px-4 py-2 text-white disabled:opacity-50
                     dark:bg-white dark:text-tinte"
        >
          {laeuft ? "…" : "Angaben speichern"}
        </button>
        {gespeichert && <span className="text-sm text-black/50">gespeichert</span>}
      </div>
    </form>
  );
}
